import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FaMapMarkerAlt,
  FaRoad,
  FaMoneyBillAlt,
  FaSpinner,
  FaCalendarAlt,
  FaClock,
} from "react-icons/fa";
import { green } from "@mui/material/colors";
import { fetchConfirmTripSignature } from "../../Services/ApiCallsServices/HomeDashboardApi";
import { useAuth } from "../../context/AuthContext";
import { useSelector, useDispatch } from "react-redux";
import { setTripInfo } from "../../Reducers/tripSlice";
import moment from 'moment';
import {encryptData} from "../../utils/encryption";

const ConfirmTrip = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasFetched = useRef(false);

  const {
    pickupLocation = "Pickup Location not found",
    dropoffLocation = "Dropoff Location not found",
    distance = "Distance not found",
    amount = 0,
    reference = "Reference not found",
    selectedDate = "Date not found",
    selectedTime = "Time not found",
    duration = "Duration not found"
  } = location.state || {};


  const dispatch = useDispatch();
  const [signature, setSignature] = useState(null);
  const [merchantId, setMerchantId] = useState(null);
  const [merchantKey, setMerchantKey] = useState(null);
  const [pfHost, setPfHost] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const { auth } = useAuth();
  const initialProfileValues = {
    email: auth.user?.email ?? "",
    firstName: auth.user?.first_name ?? "Guest",
    lastName: auth.user?.last_name ?? "User",
  };

  const { firstName, lastName, email } = initialProfileValues;
  const isTripDataMissing = !amount || !reference || !email;

  useEffect(() => {
    if (isTripDataMissing || hasFetched.current) return;
    hasFetched.current = true;
    setLoading(true);

    fetchConfirmTripSignature({
      firstName,
      lastName,
      email,
      reference,
      amount,
    })
        .then((result) => {
          setSignature(result.signature);
          setMerchantId(result.merchant_id);
          setMerchantKey(result.merchant_key);
          setPfHost(result.pfHost);
        })
        .catch((err) => {
          setError(`Payment initialization failed: ${err.message}`);
        })
        .finally(() => {
          setLoading(false);
        });
  }, [isTripDataMissing, firstName, lastName, email, reference, amount]);

  if (isTripDataMissing) {
    return <p>Critical trip information is missing. Please go back and try again.</p>;
  }

  const handleEdit = () => navigate(-1);

  const handleSubmit = (e) => {
    if (!signature) {
      e.preventDefault();
      setError("Please wait, the signature is still being generated.");
    } else {
      const tripPayload = {
        pickupLocation,
        dropoffLocation,
        selectedDate,
        selectedTime,
        distance,
        amount,
        reference,
        duration,
        firstName,
        lastName,
        email,
      };

      dispatch(setTripInfo(tripPayload));

      encryptData('qwertyuiop', JSON.stringify(tripPayload));
    }
  };


  const formattedDate = moment(selectedDate).format('Do MMMM YYYY');

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl bg-white rounded-xl shadow-md overflow-hidden">
        <div className="p-6 md:p-8">
          <h1 className="text-2xl font-bold text-gray-800 mb-6 pb-2 border-b border-yellow-500">
            Confirm Your Trip
          </h1>

          <div className="space-y-4 mb-6">
            <div className="flex items-start space-x-4">
              <FaMapMarkerAlt className="text-yellow-500 mt-1 flex-shrink-0" />
              <div>
                <h2 className="font-semibold text-gray-700">Pickup Location:</h2>
                <p className="text-gray-600">{pickupLocation}</p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <FaMapMarkerAlt className="text-yellow-500 mt-1 flex-shrink-0" />
              <div>
                <h2 className="font-semibold text-gray-700">Dropoff Location:</h2>
                <p className="text-gray-600">{dropoffLocation}</p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <FaRoad className="text-yellow-500 mt-1 flex-shrink-0" />
              <div>
                <h2 className="font-semibold text-gray-700">Distance:</h2>
                <p className="text-gray-600">{distance} Km</p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <FaCalendarAlt className="text-yellow-500 mt-1 flex-shrink-0" />
              <div>
                <h2 className="font-semibold text-gray-700">Scheduled Date:</h2>
                <p className="text-gray-600">{formattedDate}</p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <FaClock className="text-yellow-500 mt-1 flex-shrink-0" />
              <div>
                <h2 className="font-semibold text-gray-700">Scheduled Time:</h2>
                <p className="text-gray-600">{selectedTime}</p>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg mb-6">
            <span className="font-medium text-gray-700">Amount:</span>
            <div className="flex items-center space-x-2">
              <FaMoneyBillAlt className={green[500]} />
              <span className="text-xl font-bold text-gray-800">R{amount}</span>
            </div>
          </div>

          <div className="flex flex-col space-y-4 md:flex-row md:space-y-0 md:space-x-4">
            <button
              onClick={handleEdit}
              className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 transition-colors"
            >
              Edit Trip
            </button>

            <form 
              action={pfHost} 
              method="post" 
              onSubmit={handleSubmit}
              className="flex-1"
            >
              <input type="hidden" name="merchant_id" value={merchantId || ""} />
              <input type="hidden" name="merchant_key" value={merchantKey || ""} />
              <input type="hidden" name="return_url" value="https://www.storagesprint.co.za/payment-success" />
              <input type="hidden" name="cancel_url" value="https://www.storagesprint.co.za/payment-cancel" />
              <input type="hidden" name="name_first" value={firstName} />
              <input type="hidden" name="name_last" value={lastName} />
              <input type="hidden" name="email_address" value={email} />
              <input type="hidden" name="m_payment_id" value={reference} />
              <input type="hidden" name="amount" value={amount} />
              <input type="hidden" name="item_name" value={reference} />
              <input type="hidden" name="signature" value={signature || ""} />

              <button
                type="submit"
                disabled={!signature || loading}
                className={`w-full px-6 py-2 rounded-md text-white transition-colors ${
                  !signature || loading 
                    ? 'bg-yellow-400 cursor-not-allowed' 
                    : 'bg-yellow-500 hover:bg-yellow-600'
                }`}
              >
                {loading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <FaSpinner className="animate-spin" />
                    <span>Processing...</span>
                  </div>
                ) : (
                  "Pay Now"
                )}
              </button>
            </form>
          </div>

          {error && (
            <div className="mt-4 p-3 bg-red-100 text-red-700 rounded-md">
              {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConfirmTrip;
